/**
 * E69 — geocode result → Bundesland + postcode-mismatch detection. Pure and
 * DOM-free so both live failure cases (Stuttgart typed 70173, resolved Bad
 * Cannstatt 70372; Rosenheim typed 83022, resolved 83131) run as unit tests.
 *
 * Why it matters: detection survives a stale Bundesland; the REGULATORY step
 * does not — Netzanmeldung and Netzbetreiber rules key on it. And a geocoder
 * that lands on the wrong Marktplatz must be called out visibly, not in
 * small print.
 */

/** The 16 dropdown values (Project.tsx STATES) — resolved names must map onto these. */
export const GERMAN_STATES = [
  "Baden-Württemberg", "Bayern", "Berlin", "Brandenburg", "Bremen", "Hamburg", "Hessen",
  "Mecklenburg-Vorpommern", "Niedersachsen", "Nordrhein-Westfalen", "Rheinland-Pfalz",
  "Saarland", "Sachsen", "Sachsen-Anhalt", "Schleswig-Holstein", "Thüringen",
] as const;

/**
 * Map Nominatim's resolved address to a dropdown value. City-states come back
 * as `city` with no/odd `state`; anything unmappable returns null — the field
 * is then LEFT ALONE rather than guessed.
 */
export function resolvedBundesland(state: string | null, city: string | null): string | null {
  for (const cand of [state, city]) {
    if (cand && (GERMAN_STATES as readonly string[]).includes(cand)) return cand;
  }
  return null;
}

/** First 5-digit German PLZ in the typed address, if any. */
export function extractTypedPostcode(addressInput: string): string | null {
  const m = /\b(\d{5})\b/.exec(addressInput);
  return m ? m[1]! : null;
}

/**
 * The visible-warning contract: warn exactly when the planner TYPED a PLZ and
 * the geocoder resolved a DIFFERENT one. No typed PLZ → nothing to compare;
 * matching PLZ → no warning.
 */
export function postcodeMismatch(
  addressInput: string,
  resolvedPostcode: string | null,
): { typed: string; resolved: string } | null {
  const typed = extractTypedPostcode(addressInput);
  if (!typed || !resolvedPostcode) return null;
  return typed === resolvedPostcode ? null : { typed, resolved: resolvedPostcode };
}
